import React, { useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faArrowRight,
  faMagnifyingGlass,
} from "@fortawesome/free-solid-svg-icons";
import pagesData from "../../JsonFiles/Projects.json";
import IntroTemplate from "../../components/IntroTemplate/IntroTemplate";
import TechStack from "../../components/TechStack/TechStack";
import GetInTouch from "../../components/GetInTouch/GetInTouch.js";
import CaseStudies from "../../components/CaseStudies/CaseStudies.js";
import CardSliderWORK from "../../components/CardSliderWORK/CardSliderWORK.js";
import Faqs from "../../components/Faqs/Faqs.js";
import Bulleten from "../../components/Bulleten/Bulleten.js";
import "./Projects.css";

const PROJECTS = Array.isArray(pagesData) ? pagesData.filter(Boolean) : [];

const asText = (value, fallback = "") => {
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }

  return fallback;
};

const ScrollLink = ({ to, children, ...props }) => {
  const handleClick = () => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };

  return (
    <RouterLink to={to} onClick={handleClick} {...props}>
      {children}
    </RouterLink>
  );
};

function ProjectCard({ project, sequence }) {
  const [imageFailed, setImageFailed] = useState(false);
  const title = asText(project.title ?? project.name, "Axiomos project");
  const industry = asText(
    project.industry ?? project.category ?? project.type,
    "Digital solution",
  );
  const description = asText(
    project.descriptionsmall ?? project.summary ?? project.description,
    "A focused digital solution designed to improve operations and support measurable business outcomes.",
  );
  const image = asText(
    project.photo ??
      project.image ??
      project.coverImage ??
      project.thumbnail,
  );
  const fallback =
    title.replace(/[^a-zA-Z0-9]/g, "").slice(0, 2).toUpperCase() || "AX";

  return (
    <article className="project-list-card">
      <ScrollLink
        to={`/projects/${project.id}`}
        className="project-list-card__link"
        aria-label={`View the ${title} case study`}
      >
        <div className="project-list-card__media">
          {image && !imageFailed ? (
            <img
              src={image}
              alt={`${title} project preview`}
              loading="lazy"
              decoding="async"
              onError={() => setImageFailed(true)}
            />
          ) : (
            <span className="project-list-card__fallback" aria-hidden="true">
              {fallback}
            </span>
          )}

          <div className="project-list-card__overlay" aria-hidden="true" />

          <div className="project-list-card__media-meta">
            <span>{industry}</span>
            <small aria-hidden="true">
              Project / {String(sequence).padStart(2, "0")}
            </small>
          </div>
        </div>

        <div className="project-list-card__content">
          <p className="project-list-card__label">Selected case study</p>
          <h2>{title}</h2>
          <p className="project-list-card__description">{description}</p>

          <span className="project-list-card__action">
            Discover the project
            <span aria-hidden="true">
              <FontAwesomeIcon icon={faArrowRight} />
            </span>
          </span>
        </div>
      </ScrollLink>
    </article>
  );
}

function ProjectsListPages() {
  const [activeIndustry, setActiveIndustry] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");

  const industries = useMemo(() => {
    const values = PROJECTS.map((project) =>
      asText(project.industry ?? project.category ?? project.type),
    ).filter(Boolean);

    return ["All", ...new Set(values)];
  }, []);

  const filteredProjects = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return PROJECTS.filter((project) => {
      const industry = asText(
        project.industry ?? project.category ?? project.type,
      );
      const matchesIndustry =
        activeIndustry === "All" || industry === activeIndustry;
      const searchableText = [
        project.title,
        project.name,
        project.description,
        project.descriptionsmall,
        industry,
      ]
        .map((value) => asText(value))
        .join(" ")
        .toLowerCase();

      return matchesIndustry && (!query || searchableText.includes(query));
    });
  }, [activeIndustry, searchQuery]);

  return (
    <main className="projects-listing">
      <div className="projects-listing__decoration" aria-hidden="true" />

      <div className="projects-listing__container">
        <header className="projects-listing__header">
          <div>
            <p className="projects-listing__eyebrow">
              <span aria-hidden="true" />
              Selected work
            </p>
            <h1>
              Digital products built to solve
              <span> real operational problems.</span>
            </h1>
          </div>

          <div className="projects-listing__intro">
            <p>
              Explore how Axiomos transforms complex requirements into focused,
              scalable products across industries and business functions.
            </p>
            <span>
              <strong>{String(PROJECTS.length).padStart(2, "0")}</strong>
              Published case studies
            </span>
          </div>
        </header>

        <div className="projects-listing__toolbar">
          <div
            className="projects-listing__filters"
            role="group"
            aria-label="Filter projects by industry"
          >
            {industries.map((industry) => (
              <button
                type="button"
                key={industry}
                className={activeIndustry === industry ? "is-active" : ""}
                onClick={() => setActiveIndustry(industry)}
                aria-pressed={activeIndustry === industry}
              >
                {industry}
              </button>
            ))}
          </div>

          <label className="projects-listing__search">
            <FontAwesomeIcon icon={faMagnifyingGlass} aria-hidden="true" />
            <span className="sr-only">Search case studies</span>
            <input
              type="search"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search projects"
            />
          </label>
        </div>

        {filteredProjects.length > 0 ? (
          <div className="projects-listing__grid">
            {filteredProjects.map((project, index) => (
              <ProjectCard
                key={project.id ?? project.title ?? index}
                project={project}
                sequence={index + 1}
              />
            ))}
          </div>
        ) : (
          <div className="projects-listing__empty" role="status">
            <p>No projects match the selected filters.</p>
            <button
              type="button"
              onClick={() => {
                setActiveIndustry("All");
                setSearchQuery("");
              }}
            >
              Clear filters
            </button>
          </div>
        )}
      </div>
    </main>
  );
}

function ProjectsPage() {
  const { id } = useParams();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [id]);

  const page = PROJECTS.find((project) => String(project.id) === String(id));

  if (!page) {
    return (
      <main className="project-not-found">
        <div className="project-not-found__card">
          <span aria-hidden="true">404</span>
          <p className="project-not-found__eyebrow">Project not found</p>
          <h1>This case study may have moved or is no longer available.</h1>
          <ScrollLink to="/projects" className="project-not-found__link">
            <FontAwesomeIcon icon={faArrowLeft} aria-hidden="true" />
            Return to all projects
          </ScrollLink>
        </div>
      </main>
    );
  }

  const title = asText(page.title ?? page.name, "Axiomos project");
  const description = asText(
    page.description ?? page.summary,
    "A focused digital solution designed around measurable business outcomes.",
  );
  const industry = asText(
    page.industry ?? page.category ?? page.type,
    "Selected project",
  );
  const image = asText(
    page.photo ?? page.image ?? page.coverImage ?? page.thumbnail,
  );
  const deliveryCards = Array.isArray(page.card) ? page.card.filter(Boolean) : [];
  const advantages = Array.isArray(page.advantages)
    ? page.advantages.filter(Boolean)
    : [];
  const faqs = Array.isArray(page.faqs) ? page.faqs.filter(Boolean) : [];

  return (
    <main className="project-detail">
      <IntroTemplate
        id={`project-${page.id}-introduction`}
        title={title}
        description={description}
        image={image}
        eyebrow={industry}
        ctaLabel="Discuss a similar project"
        ctaTo="/contact"
      />

      {deliveryCards.length > 0 && (
        <CardSliderWORK
          id={`project-${page.id}-delivery`}
          data={deliveryCards}
          eyebrow="Delivery approach"
          heading="How the solution came together."
          introduction="A structured view of the thinking, capabilities, and execution behind this project."
        />
      )}

      {advantages.length > 0 && (
        <Bulleten
          id={`project-${page.id}-outcomes`}
          data={advantages}
          title={title}
          eyebrow="Project value"
          introduction="The practical strengths and business value designed into this solution."
        />
      )}

      <TechStack id={`project-${page.id}-technology`} />
      <CaseStudies />

      {faqs.length > 0 && <Faqs data={faqs} />}

      <GetInTouch
        id={`project-${page.id}-enquiry`}
        eyebrow="Plan your next project"
        title="Have a similar challenge? Let’s shape the right solution."
        description={`Tell us what you would like to improve or build. We’ll use the context from ${title} to begin a practical conversation about your goals.`}
        subjectLabel="Project type or area of interest"
        subjectPlaceholder="e.g. Web platform, mobile app, or automation"
        messageLabel="Tell us about your project"
        messagePlaceholder="Share the challenge, desired outcome, timeline, or any important requirements."
        panelEyebrow="Axiomos partnership"
        panelTitle="Turn complex operational needs into dependable digital products."
        whatsappMessage={`Hi, I'd like to discuss a project similar to ${title}.`}
      />
    </main>
  );
}

export { ProjectsPage, ProjectsListPages };
